function stripTrailingZeros(digits) {
    const zeroRegex = digits.match(/^0*(.+)/);
    const nonZeroRegex = /[1-9]/;
    if (!nonZeroRegex.test(digits)) {
        return '';
    }
    if (zeroRegex) {
        const nonZeroPart = zeroRegex[1];
        let lastNonZeroIndex = nonZeroPart.length - 1;
        while (lastNonZeroIndex >= 0 && nonZeroPart[lastNonZeroIndex] === '0') {
            lastNonZeroIndex--;
        }
        return zeroRegex[0].slice(0, zeroRegex[0].length - (nonZeroPart.length - lastNonZeroIndex - 1));
    }
    return digits;
}

export default {
    getQuantity(entity) {
        if (!entity) return;
        const quantity = entity.quantity_used_in_decimal || entity.quantity_in_decimal_used || entity.quantity_used // entity type is tier object
            || entity.quantity_in_decimal // entity type is line item
            || entity.plan_quantity_in_decimal
            || entity.quantity_in_decimal || entity.quantity // entity type applicable for plans or addons
            || entity.plan_quantity; // entity type for portal subscription
        return quantity;
    },
    /**
     * Strips all trailing fractional zeros (1.00 → 1, 1.7600 → 1.76, 1.6000 → 1.6).
     */
    removeTrailingZeros(value) {
        if (value == null || value === '') return value;
        const str = String(value);
        if (!str.includes('.')) return str;
        const [wholePart, decimalPart] = str.split('.');
        let i = decimalPart.length;
        while (i > 0 && decimalPart[i - 1] === '0') i--;
        const trimmedDecimal = decimalPart.slice(0, i);
        return trimmedDecimal ? `${wholePart}.${trimmedDecimal}` : wholePart;
    },
    /**
     * Trims trailing zeros while preserving at least 2 fractional digits.
     */
    trimTrailingZerosPreservingTwoDecimals(input, separator = '.') {
        if (input == null) return input;
        const str = String(input);
        const sepIndex = str.lastIndexOf(separator);
        if (sepIndex === -1) return str;
        const wholePart = str.substring(0, sepIndex);
        const decimalPart = str.substring(sepIndex + 1);
        if (decimalPart.length > 2) {
            const firstDigits = decimalPart.substring(0, 2);
            const endDigits = decimalPart.substring(2);
            return `${wholePart}${separator}${firstDigits}${stripTrailingZeros(endDigits)}`;
        }
        return `${wholePart}${separator}${decimalPart}`;
    },
    truncateDecimals(value, truncateTrailingZerosFlag = true) {
        if (value == null || value === '') return value;
        const str = String(value);
        const dotIndex = str.indexOf('.');
        if (truncateTrailingZerosFlag && dotIndex !== -1) {
            const [wholePart, decimalPart] = str.split('.');
            if (parseFloat(decimalPart) === 0) {
                return wholePart;
            }
        }
        if (dotIndex === -1 || str.length - dotIndex <= 5) {
            return str;
        }
        return str.substring(0, dotIndex + 5) + '...';
    },
    /**
     * Rounds excess fractional digits to scale (matches backend mdsPriceForDisplay / mdsQuantityForDisplay),
     * then applies trimTrailingZerosPreservingTwoDecimals for display.
     */
    normalizeMdsDecimalForDisplay(value, scale) {
        if (value == null || value === '') return value;
        const str = String(value);
        if (!str.includes('.')) return str;
        const num = Number(str);
        if (!Number.isFinite(num)) return this.trimTrailingZerosPreservingTwoDecimals(str);
        const fractionDigits = typeof scale === 'number' ? scale : 2;
        const rounded = fractionDigits > 0
            ? (Math.round(num * Math.pow(10, fractionDigits)) / Math.pow(10, fractionDigits)).toFixed(fractionDigits)
            : String(Math.round(num));
        return this.trimTrailingZerosPreservingTwoDecimals(rounded);
    },
    getPriceDisplayScale(currencyCode) {
        let currencyMax = 2;
        if (currencyCode && typeof window !== 'undefined' && window.currency_list && window.currency_list[currencyCode]) {
            const maximumFractionDigits = window.currency_list[currencyCode].maximumFractionDigits;
            currencyMax = maximumFractionDigits != null ? maximumFractionDigits : currencyMax;
        }
        const mds = typeof window !== 'undefined' && window.initInfo && window.initInfo.multi_decimal;
        if (mds && mds.enabled && mds.price_length) {
            return Math.min(mds.price_length, currencyMax);
        }
        return currencyMax;
    },
    getQuantityDisplayScale() {
        const mds = typeof window !== 'undefined' && window.initInfo && window.initInfo.multi_decimal;
        if (mds && mds.enabled && mds.quantity_length) {
            return mds.quantity_length;
        }
        return 2;
    },
    formatQuantityDisplay(quantity) {
        if (quantity == null || quantity === '') return quantity;
        const scale = this.getQuantityDisplayScale();
        const normalized = this.normalizeMdsDecimalForDisplay(quantity, scale);
        return this.truncateDecimals(normalized, true);
    },
    getDefaultQuantity() {
        return (window.initInfo.multi_decimal && window.initInfo.multi_decimal.enabled) ? "1" : undefined;
    },
    getInitialQuantity(entity) {
        if (!entity) return;
        const quantity = entity.plan_quantity_in_decimal || entity.plan_quantity ||
            entity.quantity_in_decimal || entity.quantity;
        return quantity;
    },
    getFreeQuantity(entity) {
        if (!entity) return;
        let quantity = entity.free_quantity_in_decimal || entity.free_quantity;
        if (parseFloat(quantity) == 0) return 0;
        return quantity;
    },
    getUnitAmount(entity) {
        if (!entity) return;
        const unitAmount = entity.unit_amount_in_decimal ||
            entity.unit_amount_in_decimal || //entity type is tier for portal subscription
            entity.unit_amount;
        return unitAmount;
    },
    getAmount(entity) {
        if (!entity) return;
        const amount = entity.amount_in_decimal || entity.amount;
        return amount;
    },
    isMultiDecimal(item) {
        return item && (!!item.amount_in_decimal
            || !!item.plan_amount_in_decimal
            || !!item.quantity_in_decimal
            || !!item.plan_quantity_in_decimal
        );

    },
    isGreaterThan(value1, value2) {
        return parseFloat(value1) > parseFloat(value2) ? true : false;
    },
    parseValue(value, options) {
        if (value
            && typeof value === 'string'
            && value.includes(".")) {
            return (options && options.raw_float) ? value : parseFloat(value);
        }
        return (options && options.raw_integer) ? value : parseInt(value);
    }
    // getUnitAmount(entity1, entity2) {
    //     const unitAmount = (entity1 && entity1.unit_amount_in_decimal)
    //         || (entity2 && entity2.unit_amount_in_decimal) || (entity1 && entity1.unit_amount)
    //         || (entity2 && entity2.unit_amount)
    //     return unitAmount;
    // },
}
